import { motion } from 'framer-motion';
import { useMemo } from 'react';
import { useWindowSize } from 'usehooks-ts';
import { BASE_URL } from '../../../../Config/ApiConfig';
import { Image } from '../../../../components/Image';
import { OrbitingChain } from './OrbitingChain';

const ITEM_SIZE = 72;
const INITIAL_OFFSET = 352;
const MOBILE_INITIAL_OFFSET = 153;
const ORBIT_RADIUS = 145;
const Y_AXIS_OFFSET = 0;
const MOBILE_Y_AXIS_OFFSET = 40;

const mainPageHeroChains = [
  {
    icon: BASE_URL + '/static/networks/blast.png',
    x: INITIAL_OFFSET + 256,
    y: 176 - Y_AXIS_OFFSET,
    mobileX: -352 - 180,
    mobileY: -104 - MOBILE_Y_AXIS_OFFSET,
    style: { opacity: 0.3 },
    angle: 0,
  },
  {
    icon: BASE_URL + '/static/networks/zkSyncEra.svg',
    x: -INITIAL_OFFSET - ITEM_SIZE - 63,
    y: -ITEM_SIZE - 2 - Y_AXIS_OFFSET,
    mobileX: -MOBILE_INITIAL_OFFSET - 15,
    mobileY: -12 - MOBILE_Y_AXIS_OFFSET,
    angle: 30,
  },
  {
    icon: BASE_URL + '/static/networks/ethereum.svg',
    x: -INITIAL_OFFSET - ITEM_SIZE - 231,
    y: -ITEM_SIZE + 65 - Y_AXIS_OFFSET,
    mobileX: -MOBILE_INITIAL_OFFSET + 123,
    mobileY: -19 - MOBILE_Y_AXIS_OFFSET,
    angle: 60,
  },
  {
    icon: BASE_URL + '/static/networks/base.svg',
    x: -INITIAL_OFFSET - ITEM_SIZE - 99,
    y: 124 - Y_AXIS_OFFSET,
    mobileX: -INITIAL_OFFSET - ITEM_SIZE - 99,
    mobileY: 124 - MOBILE_Y_AXIS_OFFSET,
    angle: 90,
  },
  {
    icon: BASE_URL + '/static/networks/optimism.svg',
    x: -INITIAL_OFFSET - ITEM_SIZE - 216,
    y: 248 - Y_AXIS_OFFSET,
    mobileX: -INITIAL_OFFSET - ITEM_SIZE - 216,
    mobileY: 248 - MOBILE_Y_AXIS_OFFSET,
    style: { opacity: 0.2 },
    angle: 120,
  },
  {
    icon: BASE_URL + '/static/networks/arbitrum.svg',
    x: -INITIAL_OFFSET + 50,
    y: -168 - Y_AXIS_OFFSET,
    mobileX: -MOBILE_INITIAL_OFFSET + 53,
    mobileY: -48 - MOBILE_Y_AXIS_OFFSET,
    angle: 150,
  },
  {
    icon: BASE_URL + '/static/networks/ton.svg',
    x: -INITIAL_OFFSET + ITEM_SIZE + 150,
    y: -94 - Y_AXIS_OFFSET,
    mobileX: 168,
    mobileY: -88 - MOBILE_Y_AXIS_OFFSET,
    angle: 180,
  },
  {
    icon: require('@/Assets/Icons/networks/solana.png'),
    x: ITEM_SIZE - 14,
    y: -98 - Y_AXIS_OFFSET,
    mobileX: -MOBILE_INITIAL_OFFSET + 123 + 72,
    mobileY: -19 - MOBILE_Y_AXIS_OFFSET,
    angle: 210,
  },
  {
    icon: BASE_URL + '/static/networks/scroll_network.svg',
    x: INITIAL_OFFSET - 54,
    y: -98 - Y_AXIS_OFFSET,
    mobileX: -MOBILE_INITIAL_OFFSET - 6,
    mobileY: -88 - MOBILE_Y_AXIS_OFFSET,
    angle: 240,
  },
  {
    icon: BASE_URL + '/static/networks/linea.svg',
    x: INITIAL_OFFSET + 143,
    y: -76 - Y_AXIS_OFFSET,
    mobileX: -MOBILE_INITIAL_OFFSET + 328,
    mobileY: -12 - MOBILE_Y_AXIS_OFFSET,
    angle: 270,
  },
  {
    icon: BASE_URL + '/static/networks/starkNet.svg',
    x: INITIAL_OFFSET + 112,
    y: 64 - Y_AXIS_OFFSET,
    mobileX: -352 - 182,
    mobileY: -104 - MOBILE_Y_AXIS_OFFSET,
    angle: 300,
  },
  {
    icon: BASE_URL + '/static/networks/avalanche.svg',
    x: INITIAL_OFFSET + 244,
    y: 6 - Y_AXIS_OFFSET,
    mobileX: -MOBILE_INITIAL_OFFSET + 263,
    mobileY: -48 - MOBILE_Y_AXIS_OFFSET,
    style: { opacity: 0.6 },
    angle: 330,
  },
];

export function HeroBg({
  animationStage,
  setAnimationStage,
}: {
  animationStage: 'initial' | 'orbit' | 'spinning';
  setAnimationStage: (stage: 'initial' | 'orbit' | 'spinning') => void;
}) {
  const { width, height } = useWindowSize();
  const isMobile = useMemo(() => width < 768, [width]);

  const centerY = useMemo(() => height / 2 - ORBIT_RADIUS - 120, [height]);

  const getInitialValues = (
    chain: any,
    stage: 'initial' | 'orbit' | 'spinning'
  ) => {
    switch (stage) {
      case 'orbit':
        return {
          x: isMobile ? chain.mobileX : chain.x,
          y: isMobile ? chain.mobileY : chain.y,
          opacity: 1,
        };

      default:
        return {
          x: 0,
          y: -Y_AXIS_OFFSET,
        };
    }
  };

  const getAnimateValues = (
    chain: any,
    stage: 'initial' | 'orbit' | 'spinning'
  ) => {
    const orbitX = Math.cos((chain.angle * Math.PI) / 180) * ORBIT_RADIUS;
    const orbitY =
      centerY + Math.sin((chain.angle * Math.PI) / 180) * ORBIT_RADIUS;

    switch (stage) {
      case 'orbit':
        return {
          x: orbitX,
          y: orbitY,
          opacity: 1,
          width: '60px',
          height: '60px',
          padding: '15px',
        };

      default:
        return {
          x: isMobile ? chain.mobileX : chain.x,
          y: isMobile ? chain.mobileY : chain.y,
        };
    }
  };

  return (
    <div className="main-page--hero-chains">
      {mainPageHeroChains.map((chain, i) => {
        switch (animationStage) {
          case 'spinning':
            return (
              <OrbitingChain
                key={i}
                chain={chain}
                radius={ORBIT_RADIUS}
                centerY={centerY}
              >
                <Image src={chain.icon} />
              </OrbitingChain>
            );
          default:
            return (
              <motion.div
                key={i}
                initial={getInitialValues(chain, animationStage)}
                animate={getAnimateValues(chain, animationStage)}
                transition={{
                  duration: 0.75,
                }}
                onAnimationComplete={() => {
                  if (animationStage === 'orbit') setAnimationStage('spinning');
                }}
                className="main-page--hero-chain"
                style={chain.style}
              >
                <Image withSmoothLoading src={chain.icon} />
              </motion.div>
            );
        }
      })}
    </div>
  );
}
